import { eventEndsAt, eventHasEnded } from "../track-switch/event-end";
import { MAX_AHEAD_DAYS } from "../admin/scheduled-announcements";

/**
 * The "After the event" section (TNG-96): when it shows, and when the
 * notification that announces it goes out.
 *
 * The section shows once two things hold: the organiser switched it on
 * (`configAfterEvent`), and the event has ended on its own clock, which is
 * midnight after `endDate` in the event's time zone (eventEndsAt). Rules
 * cannot evaluate a time zone, so this is the page's decision; the summary
 * document itself is readable to members at any time.
 */
export function afterEventVisible(
  event: {
    configAfterEvent?: boolean;
    endDate: string;
    timezone: string;
  },
  now: Date,
): boolean {
  return (
    event.configAfterEvent === true &&
    eventHasEnded(event.endDate, event.timezone, now)
  );
}

/** The default text of the "summary is ready" notification. Editable. */
export const DEFAULT_NOTIFICATION = {
  title: "The event summary is ready",
  body: "Hi {first_name}, thank you for coming. The summary, the slides and who wanted to talk about what are now on the event's Overview.",
} as const;

/**
 * Below this lead a booking is refused by the rules before it lands (sendAt
 * must be after the server's time when the command is written), so the
 * notification is sent now instead.
 */
export const MIN_SCHEDULE_LEAD_MS = 60_000;

export type NotificationPlan =
  | { mode: "schedule"; sendAt: Date }
  | { mode: "now" }
  | { mode: "unavailable"; problem: string };

/**
 * How the notification goes out when the section is switched on at `now`:
 * booked for the instant the event ends, or sent at once when that instant
 * has already passed (the "Send now" offer).
 */
export function notificationPlan(
  endDate: string,
  timeZone: string,
  now: Date,
): NotificationPlan {
  const end = eventEndsAt(endDate, timeZone);
  if (end === null)
    return {
      mode: "unavailable",
      problem:
        "The event's end date or time zone is not valid, so no send time can be worked out.",
    };
  const lead = end.getTime() - now.getTime();
  if (lead < MIN_SCHEDULE_LEAD_MS) return { mode: "now" };
  if (lead > MAX_AHEAD_DAYS * 24 * 60 * 60 * 1000)
    return {
      mode: "unavailable",
      problem: `The event ends more than ${MAX_AHEAD_DAYS} days from now, which is further ahead than an announcement can be booked. Switch the section on without a notification and book one later in the Announcements tab.`,
    };
  return { mode: "schedule", sendAt: end };
}

/** "00:00 on Fri 11 Sep 2026 (Berlin time)", on the event's clock. */
export function describeInstant(instant: Date, timeZone: string): string {
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(instant);
  const date = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  })
    .format(instant)
    .replace(",", "");
  return `${time} on ${date} (${timeZone})`;
}
